import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { AppError, toUserMessage } from "../lib/errors";
import { normalizeGroupTalkSessionHistoryList, type GroupTalkSessionHistoryItemView } from "../lib/groupTalkView";

export default function GroupTalkSessionList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<GroupTalkSessionHistoryItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.listGroupTalkSessions();
      setSessions(normalizeGroupTalkSessionHistoryList(response));
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setError(toUserMessage(errorValue));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-4xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk/group" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">続きから始める</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">Session List</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">会議セッション一覧</h1>
          <p className="mt-3 text-sm leading-7 text-[#6B5851]">
            過去の会議から、続きの相談を始められます。
          </p>

          {loading ? (
            <div className="mt-8 rounded-[1.8rem] border border-dashed border-[#D9C5B9] bg-[#FFFBF8] p-5 text-sm leading-6 text-[#6B5851]">
              セッションを読み込んでいます...
            </div>
          ) : null}

          {!loading && error ? (
            <div className="mt-8 rounded-[1.8rem] border border-[#E8B2A8] bg-[#FFF3F1] p-5 text-sm leading-6 text-[#A1493E]">
              <p>{error}</p>
              <button
                type="button"
                onClick={() => void loadSessions()}
                className="mt-4 rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-bold text-[#5D4D46]"
              >
                再試行
              </button>
            </div>
          ) : null}

          {!loading && !error && sessions.length === 0 ? (
            <div className="mt-8 rounded-[1.8rem] border border-dashed border-[#D9C5B9] bg-[#FFFBF8] p-5 text-sm leading-6 text-[#6B5851]">
              <p>まだ会議はありません。</p>
              <Link
                to="/talk/group"
                className="mt-4 inline-flex rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-bold text-[#5D4D46]"
              >
                新しく会議を始める
              </Link>
            </div>
          ) : null}

          {!loading && !error && sessions.length > 0 ? (
            <div className="mt-8 space-y-3">
              {sessions.map((session) => (
                <button
                  key={session.sessionId}
                  type="button"
                  onClick={() => navigate(`/talk/group/sessions/${session.sessionId}`)}
                  className="w-full rounded-[1.8rem] border border-[#EADBC8] bg-[#FFFBF8] px-5 py-4 text-left transition hover:bg-[#FFF7F1]"
                >
                  <p className="text-base font-black text-[#5D4D46]">{session.previewText}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-[#8F7468]">
                    <span>{session.updatedAt}</span>
                    {session.memberSummary.length > 0 ? (
                      <span>{session.memberSummary.join(" / ")}</span>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
